import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiCreditCard,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import {
  SALARY_PAYMENT_MODES,
} from "../utils/employee.constants";

import {
  formatEmployeeCurrency,
  formatEmployeeLabel,
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeSalary.css";


const SalaryPaymentModal = ({
  open,

  salary,

  onClose,

  onSuccess,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    form,
    setForm,
  ] =
    useState({
      amount: "",
      paymentMode: "CASH",
      referenceNumber: "",
      paymentDate: "",
      remark: "",
    });


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const netSalary =
    Number(
      salary?.netSalary ??
      salary
        ?.salary
        ?.netSalary ??
      salary?.payableAmount ??
      0
    );


  const paidAmount =
    Number(
      salary?.paidAmount ??
      0
    );


  const balanceAmount =
    Number(
      salary?.balanceAmount ??
      Math.max(
        netSalary -
        paidAmount,
        0
      )
    );


  useEffect(
    () => {

      if (
        !open
      ) {

        return;

      }


      const calculatedNet =
        Number(
          salary?.netSalary ??
          salary
            ?.salary
            ?.netSalary ??
          salary?.payableAmount ??
          0
        );


      const calculatedPaid =
        Number(
          salary?.paidAmount ??
          0
        );


      const balance =
        Number(
          salary?.balanceAmount ??
          Math.max(
            calculatedNet -
            calculatedPaid,
            0
          )
        );


      setForm({
        amount:
          balance >
          0
            ?
            String(
              balance
            )
            :
            "",

        paymentMode:
          "CASH",

        referenceNumber:
          "",

        paymentDate:
          new Date()
            .toISOString()
            .slice(
              0,
              10
            ),

        remark:
          "",
      });


      setError(
        ""
      );

    },
    [
      open,
      salary,
    ]
  );


  if (
    !open ||
    !salary
  ) {

    return null;

  }


  const employee =
    salary?.employeeId &&
    typeof salary.employeeId ===
      "object"
      ?
      salary.employeeId
      :
      salary?.employee ||
      {};


  const handleSubmit =
    async (
      event
    ) => {

      event.preventDefault();


      const amount =
        Number(
          form.amount
        );


      if (
        !Number.isFinite(
          amount
        ) ||
        amount <=
        0
      ) {

        setError(
          "Enter a valid payment amount."
        );

        return;

      }


      if (
        amount >
        balanceAmount
      ) {

        setError(
          "Payment amount cannot exceed the outstanding balance."
        );

        return;

      }


      try {

        setLoading(
          true
        );


        setError(
          ""
        );


        const response =
          await api.post(
            config.payEmployeeSalary(
              salary._id
            ),
            {
              amount,

              paymentMode:
                form.paymentMode,

              referenceNumber:
                form.referenceNumber
                  .trim(),

              paymentDate:
                form.paymentDate,

              remark:
                form.remark
                  .trim(),
            }
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to record salary payment."
          );

        }


        await onSuccess?.(
          response?.data
        );

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to record salary payment."
          )
        );

      } finally {

        setLoading(
          false
        );

      }

    };


  return ReactDOM
    .createPortal(

      <div className="employee-salary-modal-root">

        <button
          type="button"
          className="employee-salary-modal-backdrop"
          onClick={
            loading
              ?
              undefined
              :
              onClose
          }
          aria-label="Close"
        />


        <form
          className="employee-salary-modal employee-salary-payment-modal"
          onSubmit={
            handleSubmit
          }
        >

          <header>

            <div>

              <span>
                <FiCreditCard />
              </span>


              <div>

                <small>
                  SALARY PAYMENT
                </small>

                <h3>
                  Record Payment
                </h3>

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
            >
              <FiX />
            </button>

          </header>


          <div className="employee-salary-modal-body">

            <div className="employee-salary-payment-employee">

              <div>

                <span>
                  Employee
                </span>

                <strong>
                  {
                    employee?.name ||
                    salary?.employeeName ||
                    "-"
                  }
                </strong>

                <small>
                  {
                    employee?.employeeCode ||
                    salary?.employeeCode ||
                    "-"
                  }
                </small>

              </div>


              <div>

                <span>
                  Outstanding
                </span>

                <strong>
                  {
                    formatEmployeeCurrency(
                      balanceAmount
                    )
                  }
                </strong>

              </div>

            </div>


            <div className="employee-salary-modal-grid">

              <div>

                <label>
                  Payment Amount *
                </label>

                <input
                  type="text"
                  inputMode="decimal"
                  value={
                    form.amount
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          amount:
                            event
                              .target
                              .value
                              .replace(
                                /[^\d.]/g,
                                ""
                              ),
                        })
                      )
                  }
                />

              </div>


              <div>

                <label>
                  Payment Mode *
                </label>

                <select
                  value={
                    form.paymentMode
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          paymentMode:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                >

                  {
                    SALARY_PAYMENT_MODES.map(
                      (
                        mode
                      ) => (

                        <option
                          key={
                            mode
                          }
                          value={
                            mode
                          }
                        >
                          {
                            formatEmployeeLabel(
                              mode
                            )
                          }
                        </option>

                      )
                    )
                  }

                </select>

              </div>


              <div>

                <label>
                  Payment Date *
                </label>

                <input
                  type="date"
                  value={
                    form.paymentDate
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          paymentDate:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                />

              </div>


              <div>

                <label>
                  Reference / Transaction No.
                </label>

                <input
                  type="text"
                  value={
                    form.referenceNumber
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          referenceNumber:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                />

              </div>


              <div className="employee-salary-modal-full">

                <label>
                  Remark
                </label>

                <textarea
                  value={
                    form.remark
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          remark:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                  placeholder="Optional payment note"
                />

              </div>

            </div>


            {
              error
                ?
                (
                  <div className="employee-salary-modal-error">
                    {error}
                  </div>
                )
                :
                null
            }

          </div>


          <footer>

            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                loading
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              disabled={
                loading
              }
            >

              {
                loading
                  ?
                  "Saving..."
                  :
                  "Record Payment"
              }

            </button>

          </footer>

        </form>

      </div>,

      document.body

    );

};


export default SalaryPaymentModal;